import React, { useEffect, useState } from 'react';
import { Box, Typography, Stack } from '@mui/material';
import WidgetCard from './WidgetCard';
import MainCardTitle from './MainCardTitle';
import CenteredSpinner from '../../../components/CenteredSpinner';
import DashboardServices from '../../../services/DashboardServices';

const OrganizationPerformanceHeatmap = ({ title = 'Organization performance heatmap' }) => {
    const [loading, setLoading] = useState(true);
    const [organizations, setOrganizations] = useState([]);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            setLoading(true);
            try {
                const res = await DashboardServices.getOrganizationPerformanceHeatmap();
                const payload = res?.data ?? res;
                const dataArray = payload?.view?.data ?? payload?.data ?? [];

                const formatted = Array.isArray(dataArray) ? dataArray : [];

                if (mounted) {
                    setOrganizations(formatted);
                }
            } catch (err) {
                console.error('Failed to load organization performance heatmap', err);
            } finally {
                if (mounted) setLoading(false);
            }
        };
        load();
        return () => { mounted = false; };
    }, []);

    if (loading) {
        return (
            <WidgetCard sx={{ minHeight: 400 }}>
                <CenteredSpinner />
            </WidgetCard>
        );
    }

    if (organizations.length === 0) {
        return (
            <WidgetCard sx={{ minHeight: 400 }}>
                <MainCardTitle>{title}</MainCardTitle>
                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                    No data available
                </Typography>
            </WidgetCard>
        );
    }

    // Helper function to get color based on percentage
    // For Needs Improvement: Low % = Good (Green), High % = Bad (Red)
    // For Good: High % = Good (Green), Low % = Bad (Red)
    const getColorByPercentage = (percentage, isGood) => {
        if (isGood) {
            // High good percentage = darker green (good)
            // Low good percentage = darker red (bad)
            if (percentage >= 80) return '#2e7d32'; // Dark green (excellent)
            if (percentage >= 60) return '#43a047'; // Medium green
            if (percentage >= 40) return '#66bb6a'; // Light green
            if (percentage >= 20) return '#ef5350'; // Light red
            return '#c62828'; // Dark red (poor)
        } else {
            // High needs_improvement percentage = darker red (bad)
            // Low needs_improvement percentage = darker green (good)
            if (percentage >= 80) return '#7f0000'; // Very dark red (very bad)
            if (percentage >= 60) return '#c62828'; // Dark red
            if (percentage >= 40) return '#ef5350'; // Light red
            if (percentage >= 20) return '#66bb6a'; // Light green
            return '#2e7d32'; // Dark green (excellent)
        }
    };

    return (
        <WidgetCard sx={{ minHeight: 400 }}>
            <MainCardTitle>{title}</MainCardTitle>

            <Box sx={{ display: 'flex', gap: 4, mt: 3, alignItems: 'flex-start', position: 'relative', pl: 5 }}>
                {/* Y-axis scale */}
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    position: 'absolute',
                    left: 0,
                    top: 35,
                    height: organizations.length * 42,
                    justifyContent: 'space-between',
                    alignItems: 'flex-end',
                    pr: 1
                }}>
                    <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 500 }}>100</Typography>
                    <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 500 }}>80</Typography>
                    <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 500 }}>60</Typography>
                    <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 500 }}>40</Typography>
                    <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 500 }}>20</Typography>
                    <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 500 }}>0</Typography>
                </Box>

                {/* Color gradient bar on left - for Needs Improvement (inverted: high % = red top, low % = green bottom) */}
                <Box sx={{
                    position: 'absolute',
                    left: 32,
                    top: 35,
                    width: 6,
                    height: organizations.length * 42,
                    background: 'linear-gradient(to bottom, #7f0000, #c62828, #ef5350, #66bb6a, #2e7d32)',
                    borderRadius: 1,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }} />

                {/* Heatmap */}
                <Box sx={{ flex: 1, ml: 3, position: 'relative' }}>
                    {/* Table Header */}
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        height: 35,
                        gap: 0,
                        mb: 0.3
                    }}>
                        <Box sx={{ width: '50%', textAlign: 'center' }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary' }}>
                                Needs Improvement
                            </Typography>
                        </Box>
                        <Box sx={{ width: '50%', textAlign: 'center' }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary' }}>
                                Good
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                        {organizations.map((org, index) => (
                            <Box
                                key={org.organization_id || index}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    height: 42,
                                    gap: 0
                                }}
                            >
                                {/* Needs Improvement Bar */}
                                <Box
                                    sx={{
                                        width: '50%',
                                        height: '100%',
                                        bgcolor: getColorByPercentage(org.needs_improvement_percentage, false),
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'white',
                                        fontWeight: 600,
                                        fontSize: 14,
                                        transition: 'all 0.3s ease',
                                        position: 'relative',
                                        '&:hover': {
                                            filter: 'brightness(1.1)',
                                            cursor: 'pointer'
                                        }
                                    }}
                                    title={`Needs Improvement: ${org.needs_improvement_cases} cases (${org.needs_improvement_percentage}%)`}
                                >
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.1 }}>
                                            {org.needs_improvement_cases}
                                        </Typography>
                                        <Typography sx={{ fontSize: 10, fontWeight: 500, opacity: 0.9 }}>
                                            {org.needs_improvement_percentage}%
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Good Bar */}
                                <Box
                                    sx={{
                                        width: '50%',
                                        height: '100%',
                                        bgcolor: getColorByPercentage(org.good_percentage, true),
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'white',
                                        fontWeight: 600,
                                        fontSize: 14,
                                        transition: 'all 0.3s ease',
                                        position: 'relative',
                                        '&:hover': {
                                            filter: 'brightness(1.1)',
                                            cursor: 'pointer'
                                        }
                                    }}
                                    title={`Good: ${org.good_cases} cases (${org.good_percentage}%)`}
                                >
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.1 }}>
                                            {org.good_cases}
                                        </Typography>
                                        <Typography sx={{ fontSize: 10, fontWeight: 500, opacity: 0.9 }}>
                                            {org.good_percentage}%
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* Color gradient bar on right - for Good (normal: high % = green top, low % = red bottom) */}
                <Box sx={{
                    position: 'absolute',
                    right: 135,
                    top: 35,
                    width: 6,
                    height: organizations.length * 42,
                    background: 'linear-gradient(to bottom, #2e7d32, #66bb6a, #ef5350, #c62828, #7f0000)',
                    borderRadius: 1,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }} />

                {/* Organization Names Column */}
                <Box sx={{ minWidth: 120, flexShrink: 0 }}>
                    {/* Empty space for header alignment */}
                    <Box sx={{ height: 35, mb: 0.3 }} />
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                        {organizations.map((org, index) => (
                            <Box
                                key={org.organization_id || index}
                                sx={{
                                    height: 42,
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: 13,
                                        fontWeight: 500,
                                        color: 'text.primary',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {org.organization_short_name || org.organization_name}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>
        </WidgetCard>
    );
};

export default OrganizationPerformanceHeatmap;
